import { useTranslations } from "use-intl";

import { AddToLibraryIcon, CheckIcon, WishlistIcon } from "@/components/appIcons";
import type { AddToListsButtonState } from "@/components/lists/add-to-lists-button-state";
import { cn } from "@/lib/utils";

interface AddToListsButtonViewProps {
  state: AddToListsButtonState;
  variant: "icon" | "button" | "full";
  className?: string;
  onClick: (event?: React.MouseEvent) => void;
}

const stateStyles = {
  collection: {
    icon: CheckIcon,
    button: "bg-green-600 text-white hover:bg-green-700",
    full: "border border-green-600/30 bg-green-600/15 text-green-500 hover:bg-green-600/25",
  },
  wishlist: {
    icon: WishlistIcon,
    button: "bg-pink-600 text-white hover:bg-pink-700",
    full: "border border-pink-600/30 bg-pink-600/15 text-pink-500 hover:bg-pink-600/25",
  },
  available: {
    icon: AddToLibraryIcon,
    button: "bg-secondary text-foreground hover:bg-secondary/80",
    full: "bg-secondary text-foreground hover:bg-secondary/80",
  },
};

export function AddToListsButtonView({
  state,
  variant,
  className,
  onClick,
}: AddToListsButtonViewProps) {
  const t = useTranslations("globals.lists");
  const tc = useTranslations("shared.collection");
  const label =
    state === "collection"
      ? tc("inCollection")
      : state === "wishlist"
        ? t("inWishlist")
        : t("addToLists");
  const styles = stateStyles[state];
  const StateIcon = styles.icon;

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={t("addToLists")}
        title={t("addToLists")}
        className={cn(
          "relative after:absolute after:-inset-0.5 after:content-[''] size-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:scale-110 transition-transform",
          className,
        )}
      >
        <AddToLibraryIcon />
      </button>
    );
  }

  if (variant === "full") {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "flex flex-1 min-w-[200px] items-center justify-center gap-2 rounded-lg h-12 text-base font-bold transition-all",
          styles.full,
          className,
        )}
      >
        <StateIcon />
        {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold transition-all",
        styles.button,
        className,
      )}
    >
      <StateIcon className="text-base" />
      {label}
    </button>
  );
}
